﻿import {
  AlertTriangle,
  CheckCircle2,
  Eye,
  FileText,
  Loader2,
  Pencil,
  RotateCcw,
  Save,
} from "lucide-react";
import { useT } from "@/lib/i18n";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { LegalPageSlug } from "@ticketnjoy/types";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { MarkdownPreview, TipTapEditor } from "@/components/tiptap-editor";
import {
  useLegalOverrideQuery,
  useUpsertLegalOverrideMutation,
} from "@/features/legal/api";
import { ApiError } from "@/lib/api-client";
import { cn } from "@/lib/utils";

type Mode = "edit" | "preview-default" | "preview-current";

/**
 * Organizer'ın belirli bir yasal sayfa için override editörü.
 *
 * Mantık:
 * - Query hem platform template hem varsa override'ı döner.
 * - Editör mode: override varsa onu, yoksa template'i başlangıç olarak yükler.
 * - "Önizle (platform)" mode: düz template'i gösterir (dokunulmaz).
 * - "Önizle (mağaza)" mode: şu an kaydedilmiş effective içeriği gösterir.
 * - Kaydetme: boş/template ile aynıysa backend kaydı siler (fallback'e döner).
 *
 * Görsel: yazma alanı tek kart, üstte action barıyla.
 */
export function LegalOverrideEditor({ slug }: { slug: LegalPageSlug }) {
  const t = useT("settings.legalOverrides");
  const tCommon = useT("common");

  const query = useLegalOverrideQuery(slug);
  const upsert = useUpsertLegalOverrideMutation(slug);

  const [mode, setMode] = useState<Mode>("edit");
  const [draft, setDraft] = useState<string>("");

  // İlk data gelince (veya refetch sonrası) draft'ı yenile.
  useEffect(() => {
    if (!query.data) return;
    const initial =
      query.data.overrideMarkdown ?? query.data.defaultTemplateMarkdown;
    setDraft(initial);
  }, [query.data]);

  if (query.isLoading) {
    return (
      <Card>
        <CardContent className="flex min-h-[300px] items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {tCommon("loading")}
        </CardContent>
      </Card>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Card>
        <CardContent className="flex min-h-[200px] items-center justify-center gap-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4" />
          {query.error instanceof ApiError
            ? query.error.detail
            : tCommon("unexpectedError")}
        </CardContent>
      </Card>
    );
  }

  const { defaultTemplateMarkdown, overrideMarkdown } = query.data;
  const hasOverride = overrideMarkdown !== null;
  const isDirty =
    draft !== (overrideMarkdown ?? defaultTemplateMarkdown);
  const willRevertToDefault = draft.trim() === defaultTemplateMarkdown.trim();

  async function handleSave() {
    try {
      await upsert.mutateAsync({ contentMarkdown: draft });
      toast.success(willRevertToDefault ? t("revertedToDefault") : t("saved"));
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.detail : tCommon("unexpectedError"),
      );
    }
  }

  async function handleRevertToDefault() {
    if (!hasOverride) {
      setDraft(defaultTemplateMarkdown);
      return;
    }
    if (!window.confirm(t("revertConfirm"))) return;
    try {
      await upsert.mutateAsync({ contentMarkdown: "" });
      setDraft(defaultTemplateMarkdown);
      toast.success(t("revertedToDefault"));
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.detail : tCommon("unexpectedError"),
      );
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base">{t(`pages.${slug}`)}</CardTitle>
              {hasOverride ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-900 dark:bg-amber-950/60 dark:text-amber-200">
                  <Pencil className="h-3 w-3" />
                  {t("badgeCustom")}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-200">
                  <CheckCircle2 className="h-3 w-3" />
                  {t("badgeDefault")}
                </span>
              )}
            </div>
            <CardDescription className="text-xs">
              {t("description")}
            </CardDescription>
          </div>

          <div className="flex flex-wrap items-center gap-1 rounded-md border bg-muted/40 p-0.5">
            <ModeButton
              active={mode === "edit"}
              onClick={() => setMode("edit")}
              icon={<Pencil className="h-3.5 w-3.5" />}
              label={t("modeEdit")}
            />
            <ModeButton
              active={mode === "preview-current"}
              onClick={() => setMode("preview-current")}
              icon={<Eye className="h-3.5 w-3.5" />}
              label={t("modePreview")}
            />
            <ModeButton
              active={mode === "preview-default"}
              onClick={() => setMode("preview-default")}
              icon={<FileText className="h-3.5 w-3.5" />}
              label={t("modeDefault")}
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {mode === "edit" && (
          <>
            {willRevertToDefault && hasOverride && (
              <div className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900 dark:border-sky-900/50 dark:bg-sky-950/50 dark:text-sky-200">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{t("saveWillRevert")}</span>
              </div>
            )}
            <TipTapEditor
              value={draft}
              onChange={setDraft}
              disabled={upsert.isPending}
              minHeight="420px"
            />
          </>
        )}

        {mode === "preview-current" && (
          <MarkdownPreview
            markdown={overrideMarkdown ?? defaultTemplateMarkdown}
          />
        )}

        {mode === "preview-default" && (
          <MarkdownPreview markdown={defaultTemplateMarkdown} />
        )}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            onClick={handleRevertToDefault}
            disabled={upsert.isPending || (!hasOverride && !isDirty)}
            className="gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            {t("revertAction")}
          </Button>

          <Button
            type="button"
            onClick={handleSave}
            disabled={upsert.isPending || !isDirty || mode !== "edit"}
            className="gap-2"
          >
            {upsert.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {tCommon("save")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ModeButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition",
        active
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {icon}
      {label}
    </button>
  );
}
